import { FC, useRef } from "react";
import SearchspringProductTile from "./SearchspringProductTile";
import { styled } from "styled-components";
import { useSearchspringPLPContext } from "../contexts/SearchspringPLPContext";
import { button, colors, font } from "../lib/sharedStyles";
import getContentfulLocale from "../lib/getContentfulLocale";
import useContentful from "../hooks/useContentful";
import contentfulComponentLoop from "../lib/contentfulComponentLoop";
import PLPMenu from "./SearchspringPLP/PLPMenu";
import PLPFilterIcon from "./Icons/PLPFilterIcon";
import PLPFilterMobile from "./SearchspringPLP/PLPFilterMobile";
import PLPCompareBar from "./SearchspringPLP/PLPCompareBar";
import PLPRelatedSearchTerms from "./SearchspringPLP/PLPRelatedSearchTerms";
import { excludeFromLocale } from "../lib/excludeFromLocale";

const root = document.getElementById('react-searchspring-plp') as HTMLElement;

const PageContainer = styled.div`
  // This is the desired distance between the breadcrumbs and the
  // header menu as dictated by the design files.
  padding-top: 40px;

  @media (min-width: 1150px) { // Dawn breakpoint
    // Note: this margin is a temporary fix to escape the header menu 
    // on desktop, and should be fixed in the header stylesheet.
    margin-top: 56px;
  }
`;

const Breadcrumbs = styled.div<{ $adjustForPencilBanner: boolean; }>`
  display: flex;
  margin: 0 auto;
  max-width: 90%;
  padding: 0;

  @media (max-width: 1150px) { // Dawn breakpoint
    ${({ $adjustForPencilBanner }) => $adjustForPencilBanner && `
      padding-top: 56px;
    `}
  }

  a, span {
    ${font('Instrument Sans', 400, ' 14px', '20px')};
    letter-spacing: 0.14px;
    color: ${colors.fender.primaryDark};
    text-decoration: none;
  }
`;

const HeaderContentContainer = styled.div`
  margin-bottom: 32px;

  @media (min-width: 1024px) {
    margin-bottom: 40px;
  }
`;

const ProductsContainer = styled.div<{ $layout: string; $filterMenuOpen: boolean; }>`
  display: grid;  
  grid-row-gap: 40px;
  margin-bottom: 20px;
  
  ${({ $layout }) => $layout == 'grid' && `
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: 4px;
  `}

  ${({ $layout }) => $layout == 'list' && `  
    grid-template-columns: repeat(1, 1fr);
  `}

  @media (min-width: 1024px) {
    grid-row-gap: 4px;
    margin-bottom: 40px;
    // This should match the transition in PLPFilterDesktop OuterContainer
    transition: padding-left 600ms ease;

    ${({ $layout }) => $layout == 'grid' && `
      grid-template-columns: repeat(4, 1fr);
    `}

    ${({ $layout, $filterMenuOpen }) => $layout == 'grid' && $filterMenuOpen && `
      grid-template-columns: repeat(3, 1fr);
    `}

    ${({ $filterMenuOpen }) => $filterMenuOpen && `
      padding-left: 360px;
    `}
  }
`;

const ListFooter = styled.div`
  text-align: center;
  margin-bottom: 80px;
  > p {
    margin: 0 0 24px;
    ${font('Instrument Sans', 400, '14px', '20px')}
    letter-spacing: 0.14px;
    color: ${colors.fender.primaryDark};
  }

  > button {
    appearance: none;
    background: none;
    ${button('Black Stroke Pill', 'fender')}

    @media (max-width: 1023px) {
      padding: 10px 24px;
    }
  }
`;

const MobileFilterToggle = styled.div`
  position: fixed;
  bottom: 24px;
  width: 100%;
  display: flex;
  justify-content: center;

  button {
    ${button('Black Pill', 'fender', true)};
    border: none;
  }

  span {
    ${font('Instrument Sans', 600, '16px', '22px')}
    letter-spacing: 0.16px;
    margin-left: 8px;
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

const ContentInGridWrapper = styled.div<{ $layout: string; $columnSize: number; }>`
  position: relative;
  
  ${({ $layout }) => $layout == 'list' && `
    display: none;
  `}

  ${({ $columnSize }) => $columnSize && `
    grid-column: span ${$columnSize};
  `}
`;

const SearchHeading = styled.h1`
  ${font('Futura', 600, '28px', '28px', '-0.56px')};
  text-align: center;
  color: ${colors.fender.primaryDark};
  margin: 40px 60px;

  @media (min-width: 768px) {
    font-size: 48px;
    line-height: 44px;
    letter-spacing: -0.56px;
  }
`;

interface RenderItem {
  type: string;
  data: any;
  columnSize?: number;
}

interface SearchspringPLPProps {
  isSearchTemplate?: boolean;
  searchTerms?: string;
  // This component also renders the search results page, which is not filtered by collection.
  shopifyCollectionHandle?: string;
  shouldSliceVariants?: boolean;
  rootElementId: string;
}

const SearchspringPLP: FC<SearchspringPLPProps> = ({ 
  isSearchTemplate = false,
  searchTerms, 
  shopifyCollectionHandle,
  shouldSliceVariants = false,
  rootElementId
}) => {
  const belowGridRef = useRef<HTMLDivElement>(null);
  const { 
    activeFilters,
    compareActive,
    compareProducts,
    fetchNextPage, 
    filterMenuOpen,
    handleCompareProductClick, 
    pagination, 
    products, 
    relatedSearchTerms,
    toggleFilterMenu, 
    viewOption,
  } = useSearchspringPLPContext();

  const pencilBannerEl = document.getElementsByClassName('utility-bar');
  // This is passed to each product tile to be used in Klarna Placements.
  // Will likely need a different function to get EMEA locales for pricing,
  // but this gets us up and running in the US.
  const rootEl = document.getElementById(rootElementId) as HTMLElement;
  let localeString = rootEl?.dataset.shopLocale;
  let localeMarket = rootEl?.dataset.shopMarket;
  const locale = getContentfulLocale(localeString, localeMarket);
  const region = rootEl?.dataset.region;

  let shopifyCollectionTitle;
  let contentfulHeaderContent;
  let contentInGrid;
  let contentfulBelowGridContent;

  if (shopifyCollectionHandle) {
    const { contentfulData } = useContentful({
      'fields.shopifyCollectionHandle': shopifyCollectionHandle,
      content_type: 'productListingPage',
      include: 10,
      locale
    });
  
    shopifyCollectionTitle = root?.dataset.collectionTitle;
    contentfulHeaderContent = contentfulData?.items[0]?.fields?.header ?? [];
    contentInGrid = contentfulData?.items[0]?.fields?.contentInGrid ?? [];
    contentfulBelowGridContent = contentfulData?.items[0]?.fields?.contentBelowGrid ?? [];
  }

  let moreProductsAvailable = false;
  if (pagination.totalPages && pagination.page) {
    moreProductsAvailable = pagination.totalPages > pagination.page;
  }

  const gridRenderArray: RenderItem[] = products.map(product => ({ type: 'product', data: product }));
  if (contentInGrid) {
    // @ts-ignore
    contentInGrid.forEach(entry => {
      const renderItem = { 
        type: 'content', 
        data: entry.fields.content, 
        columnSize: entry.fields.columnSize,
        componentType: entry.fields.content.sys.contentType.sys.id
      };
          
      // Do not push into the render array if this is configured for exclusion from this locale.
      if (excludeFromLocale(locale, entry.fields.excludeFromRegions)) {
        return;
      }
      
      // Assuming merchandisers will not 0 index
      gridRenderArray.splice(entry.fields.gridPosition - 1, 0, renderItem);
    });
  }

  // @ts-ignore
  const onCompareProductClick = (e, product) => {
    e.preventDefault();
    e.stopPropagation();

    handleCompareProductClick(product);
  }

  return (
    <PageContainer>
      {(!isSearchTemplate) && (
        <Breadcrumbs $adjustForPencilBanner={(pencilBannerEl ? true : false)}>
          <a href="/">Home</a>
          <span>&nbsp;&nbsp;•&nbsp;&nbsp;</span>
          <span>{shopifyCollectionTitle}</span>
        </Breadcrumbs>
      )}
      <HeaderContentContainer>
        {/* @ts-ignore */}
        {isSearchTemplate && pagination?.totalResults >= 0 && (
          <SearchHeading>
            {pagination.totalResults} results for "{searchTerms}"
          </SearchHeading>
        )}
        {isSearchTemplate && relatedSearchTerms.length > 0 && (
          <PLPRelatedSearchTerms />
        )}
        {(!isSearchTemplate) && (
          contentfulComponentLoop(contentfulHeaderContent, {locale})
        )}
      </HeaderContentContainer>
      <PLPMenu belowGridRef={belowGridRef} isSearchTemplate={isSearchTemplate} />
      <ProductsContainer $layout={viewOption} $filterMenuOpen={filterMenuOpen}>
        {gridRenderArray.map(({ type, data, columnSize = 1 }) => {
          if (type == 'content') {
            return (
              <ContentInGridWrapper 
                // @ts-ignore
                key={data.sys.id} 
                $layout={viewOption}
                $columnSize={columnSize}
              >
                {contentfulComponentLoop([data], {isContentInGrid: true})}
              </ContentInGridWrapper>
            );
          }

          if (type == 'product') {
            // @ts-ignore
            const compareIndex = compareProducts?.findIndex(i => i.attributes.ss_id == data.attributes.ss_id);
            const isSelectedForCompare = compareIndex >= 0;

            return (
              <SearchspringProductTile 
                key={data.id} 
                isSlicedVariant={shouldSliceVariants}
                locale={localeString || 'en'}
                shopMarket={localeMarket}
                region={region}
                // @ts-ignore
                product={data} 
                layout={viewOption}
                showCompare={compareActive}
                compareClickHandler={e => onCompareProductClick(e, data)}
                isSelectedForCompare={isSelectedForCompare}
              />
            )
          }
        })}
      </ProductsContainer>
      <PLPFilterMobile />
      <MobileFilterToggle onClick={() => toggleFilterMenu()}>
        <button>
          <PLPFilterIcon theme="light" />
          {(activeFilters.length > 0) ? (
            <span>Filter & Sort By ({activeFilters.length})</span>
          ) : (
            <span>Filter & Sort By</span>
          )}
        </button>
      </MobileFilterToggle>
      <ListFooter>
        <p>Viewing {products.length} of {pagination.totalResults} products</p>
        {(moreProductsAvailable) && (
          <button onClick={() => fetchNextPage()}>Show more</button>
        )}
      </ListFooter>
      <div ref={belowGridRef}>
        {contentfulComponentLoop(contentfulBelowGridContent, {locale})}
      </div>
      {compareActive && shopifyCollectionHandle && (
        <PLPCompareBar shopifyCollectionHandle={shopifyCollectionHandle} />
      )}
    </PageContainer>
  );
}

export default SearchspringPLP;